<!-- src/views/AddEditDevicePage.vue -->
<template>
  <div class="page-content" dir="rtl">
    <div class="background-box">
      <!-- Logo Header -->
      <div class="header">
        <img :src="`${baseUrl}img/screen_logo.webp`" alt="تیراژه لوگو" class="header-logo" />
      </div>

      <!-- Form Content -->
      <div class="form-content">
        <h1>{{ isEdit ? 'ویرایش دستگاه' : 'افزودن دستگاه' }}</h1>

        <div class="form-fields">
          <!-- Device Name -->
          <div class="form-group">
            <input type="text" id="deviceName" v-model="device.name" placeholder="نام دستگاه" maxlength="20" />
          </div>

          <!-- SIM Card Number -->
          <div class="form-group">
            <input type="tel" id="devicePhone" v-model="phoneNumberDisplay" placeholder="شماره سیم‌کارت" maxlength="11"
              @input="validatePhoneNumber" />
          </div>

          <!-- Operator Selection -->
          <div class="form-group operator-buttons">
            <ImageButtonWithTextBelow v-for="operator in operatorTypes" :key="operator.id" group-id="operator-group"
              :item-id="operator.id" :image="`${baseUrl}${operator.icon}`" :text="operator.label"
              :default-selected-id="device.operator" @item-selected="handleOperatorSelected" />
          </div>

          <!-- Device Type Selection -->
          <div class="form-group device-type-buttons">
            <ImageButtonWithTextBelow v-for="type in deviceTypes" :key="type.id" group-id="device-type-group"
              :item-id="type.id" :image="`${baseUrl}${type.icon}`" :text="type.label" :default-selected-id="device.type"
              @item-selected="handleDeviceTypeSelected" />
          </div>

          <!-- PIN -->
          <div class="form-group pin-group">
            <div class="input-with-toggle">
              <input :type="showPin ? 'tel' : 'password'" id="devicePin" v-model="pinDisplay" placeholder="رمز‌ عبور"
                maxlength="4" inputmode="numeric" pattern="[0-9]*" @input="validatePin" />
              <i :class="showPin ? 'fas fa-eye-slash' : 'fas fa-eye'" class="toggle-icon"
                @click="togglePinVisibility"></i>
            </div>
          </div>

          <!-- Actions -->
          <div class="form-actions">
            <button class="save-button" @click="saveDevice">
              <span>{{ isEdit ? 'بروزرسانی' : 'ذخیره' }}</span>
              <img :src="isEdit ? `${baseUrl}img/icons/ic_edit_green.webp` : `${baseUrl}img/icons/save.webp`"
                alt="ذخیره" class="button-icon" />
            </button>
            <button class="cancel-button" @click="$router.push('/')">
              <span>انصراف</span>
              <img :src="`${baseUrl}img/icons/cancel.webp`" alt="انصراف" class="button-icon" />
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { globalState } from '../store';
import { DeviceTypeList } from '../enums/DeviceTypes';
import ImageButtonWithTextBelow from '../components/ImageButtonWithTextBelow.vue';

export default {
  name: 'AddEditDevicePage',
  components: {
    ImageButtonWithTextBelow,
  },
  data() {
    return {
      device: {
        id: null,
        name: '',
        phoneNumber: '',
        operator: 'MCI', // Default operator
        type: 'home',
        pin: '',
      },
      showPin: false,
      deviceTypes: DeviceTypeList,
      operatorTypes: [
        { id: 'MCI', label: 'همراه اول', icon: 'img/operators/mci.webp' },
        { id: 'MTN', label: 'ایرانسل', icon: 'img/operators/mtn.webp' },
        { id: 'Rightel', label: 'رایتل', icon: 'img/operators/rightel.webp' },
      ],
    };
  },
  computed: {
    baseUrl() {
      return process.env.BASE_URL;
    },
    isEdit() {
      const editingDeviceId = globalState.editingDeviceId;
      return !!editingDeviceId;
    },
    phoneNumberDisplay: {
      get() {
        return this.$utils.toPersianDigits(this.device.phoneNumber);
      },
      set(value) {
        const englishValue = this.$utils.toEnglishDigits(value);
        this.device.phoneNumber = englishValue;
        this.validatePhoneNumber();
      },
    },
    pinDisplay: {
      get() {
        return this.$utils.toPersianDigits(this.device.pin);
      },
      set(value) {
        const englishValue = this.$utils.toEnglishDigits(value);
        this.device.pin = englishValue;
        this.validatePin();
      },
    },
  },
  beforeRouteEnter(to, from, next) {
    const editingDeviceId = globalState.editingDeviceId;
    if (editingDeviceId) {
      const devices = JSON.parse(localStorage.getItem('devices')) || [];
      const device = devices.find((d) => d.id === editingDeviceId);
      if (!device) {
        globalState.clearEditingDeviceId();
        next('/');
      } else {
        next(vm => {
          vm.device = {
            ...device,
            operator: device.operator || 'MCI', // Default if missing
          };
        });
      }
    } else {
      next();
    }
  },
  beforeRouteLeave(to, from, next) {
    globalState.clearEditingDeviceId();
    next();
  },
  methods: {
    validatePhoneNumber() {
      this.device.phoneNumber = this.$utils.validatePhoneNumber(this.device.phoneNumber);
    },
    validatePin() {
      this.device.pin = this.device.pin.replace(/[^0-9]/g, '').slice(0, 4);
    },
    saveDevice() {
      if (!this.device.name) {
        alert('لطفاً نام دستگاه را وارد کنید.');
        return;
      }
      if (!this.device.phoneNumber || !/^0\d{10}$/.test(this.device.phoneNumber)) {
        alert('لطفاً یک شماره تلفن ۱۱ رقمی که با ۰ شروع می‌شود وارد کنید (مثال: ۰۹۱۲۳۴۵۶۷۸۹).');
        return;
      }
      if (!this.device.operator) {
        alert('لطفاً اپراتور سیم‌کارت را انتخاب کنید.');
        return;
      }
      if (!this.device.type) {
        alert('لطفاً نوع دستگاه را انتخاب کنید.');
        return;
      }
      if (!this.device.pin || this.device.pin.length !== 4) {
        alert('لطفاً یک رمز‌ عبور ۴ رقمی وارد کنید.');
        return;
      }

      const devices = JSON.parse(localStorage.getItem('devices')) || [];

      // Check for duplicate phone number
      const isDuplicate = devices.some(d =>
        d.phoneNumber === this.device.phoneNumber &&
        (!this.isEdit || d.id !== globalState.editingDeviceId)
      );
      if (isDuplicate) {
        alert('این شماره تلفن قبلاً برای دستگاه دیگری ثبت شده است. لطفاً شماره دیگری وارد کنید.');
        return;
      }

      if (this.isEdit) {
        const editingDeviceId = globalState.editingDeviceId;
        const index = devices.findIndex((d) => d.id === editingDeviceId);
        if (index !== -1) {
          devices[index] = { ...this.device };
        }
      } else {
        this.device.id = Date.now().toString();
        devices.push({ ...this.device });
      }
      localStorage.setItem('devices', JSON.stringify(devices));
      this.$router.push('/');
    },
    togglePinVisibility() {
      this.showPin = !this.showPin;
    },
    handleDeviceTypeSelected(itemId) {
      this.device.type = itemId;
    },
    handleOperatorSelected(itemId) {
      this.device.operator = itemId;
    },
  },
};
</script>

<style scoped>
.page-content {
  font-family: 'Vazir';
  margin: 0;
  padding: 0;
  min-height: 100vh;
  box-sizing: border-box;
  background-color: transparent;
}

.background-box {
  height: 100vh;
  background-color: #25273e;
  /* tirajehScreensBgMain */
  padding-top: 6px;
  /* Reduced from 8px */
  box-sizing: border-box;
  overflow: hidden;
}

.header {
  height: 16%;
  /* Reverted to original size */
  display: flex;
  justify-content: center;
  align-items: center;
}

.header-logo {
  max-height: 100%;
  width: auto;
}

.form-content {
  background-color: #80808e;
  /* addDevBg */
  border-radius: 64px 64px 0 0;
  /* Reduced from 84px */
  height: 84%;
  /* Reverted to original size */
  padding: 16px;
  /* Reduced from 24px */
  box-sizing: border-box;
  overflow-y: auto;
  text-align: center;
  -ms-overflow-style: none;
  /* IE and Edge */
  scrollbar-width: none;
  /* Firefox */
}

.form-content::-webkit-scrollbar {
  display: none;
  /* Chrome, Safari, and Opera */
}

.form-content h1 {
  font-size: 20px;
  /* Reduced from 24px */
  color: #ffffff;
  margin: 0 0 12px;
  /* Reduced from 16px */
}

.form-fields {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  /* Reduced from 16px */
}

.form-group {
  width: 100%;
  max-width: 400px;
  /* Reduced from 600px */
}

.form-group input {
  width: 100%;
  height: 40px;
  /* Reduced from 58px */
  background-color: #27253e;
  /* tirajehFieldsBg */
  border-radius: 32px;
  /* Reduced from 46px */
  padding: 0 12px;
  /* Reduced from 16px */
  font-size: 14px;
  /* Reduced from 16px */
  color: #d3d3d3;
  /* textColorHint */
  text-align: center;
  border: none;
  box-sizing: border-box;
  font-family: 'Vazir';
}

.pin-group {
  max-width: 140px;
  /* Reduced from 200px */
}

.input-with-toggle {
  position: relative;
  width: 100%;
}

.input-with-toggle input {
  padding-left: 30px;
  /* Reduced from 40px */
}

.toggle-icon {
  position: absolute;
  left: 8px;
  /* Reduced from 10px */
  top: 50%;
  transform: translateY(-50%);
  font-size: 18px;
  /* Reduced from 24px */
  color: #d3d3d3;
  /* Matches textColorHint */
  cursor: pointer;
}

.toggle-icon:hover {
  color: #ffffff;
}

.device-type-buttons,
.operator-buttons {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 8px;
  /* Reduced from 10px */
}

.form-actions {
  display: flex;
  flex-direction: column;
  gap: 6px;
  /* Reduced from 8px */
  margin-top: 12px;
  /* Reduced from 16px */
}

.save-button,
.cancel-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 110px;
  /* Reduced from 132px */
  padding: 12px 16px;
  /* Reduced from 10px 20px */
  background-color: #27253e;
  /* tirajehFieldsBg */
  color: #ffffff;
  border: none;
  border-radius: 48px;
  /* Reduced from 5px */
  font-size: 14px;
  /* Reduced from 16px */
  cursor: pointer;
  gap: 6px;
  /* Reduced from 8px */
}

.button-icon {
  width: 18px;
  /* Reduced from 24px */
  height: 18px;
}
</style>